import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../Layout";
import BillingTabs from "../../Billing/BillingTabs";
import BillingHeader from "../../Billing/BillingHeader";
import BillingOverview from "../../Billing/BillingOverview";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Badge } from "../../ui/badge";
import Text from "../../Tags/Text";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Coins } from "lucide-react";
import { Button } from "../../ui/button";
import Image from "../../Tags/Image";
import { useStoreBilling } from "../../../stores/useStoreBIlling";
import { useBilling } from "../../../apiHooks/useBilling";
import Plan from "./Plan";
import PlanLoader from "./PlanLoader";
import OneTime from "./OneTime";
import Enterprise from "./Enterprise";
import useRoleCheck from "../../../hooks/useRoleCheck";
import PageHeader from "../../PageHeader";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

export default function PlansPage() {
  const { activeWorkspace } = useRoleCheck();

  const { plans, metadata } = useStoreBilling();
  const { isLoading } = useBilling(activeWorkspace?.id);
  const captureEvent = useCaptureEvent();

  console.log("plans", plans);
  console.log("metadata", metadata);

  const getActivePlanInfo = (plans) => {
    if (!Array.isArray(plans)) return null;
    for (const plan of plans) {
      const pricing = plan?.pricing || {};
      for (const interval of ["monthly", "yearly"]) {
        const p = pricing[interval];
        if (p?.is_subscribed) {
          return {
            name: plan.name,
            interval, // "monthly" | "yearly"
            quantity: p.subscription_quantity ?? null,
          };
        }
      }
    }
    return null;
  };

  const PricingCards = ({ interval, plans, activePlan }) => {
    if (isLoading) {
      return (
        <div className="flex space-x-3 items-start mt-4">
          <div className="w-4/12">
            <PlanLoader />
          </div>
          <div className="w-4/12">
            <PlanLoader />
          </div>
          <div className="w-4/12">
            <PlanLoader />
          </div>
        </div>
      );
    }
    return (
      <div className="flex space-x-3 items-start mt-4">
        {plans &&
          plans.length > 0 &&
          plans.map((plan) => (
            <div className="w-4/12" key={`${plan?.id || plan?.name || interval}`}>
              <Plan
                interval={interval}
                plan={plan}
                available_quantity={plan?.pricing?.[interval].quantity_options}
                activeWorkspace={activeWorkspace}
                activePlan={activePlan}
              />
            </div>
          ))}
        <div className="w-4/12">
          <Enterprise />
        </div>
      </div>
    );
  };

  const activePlan = useMemo(() => getActivePlanInfo(plans), [plans]);
  const [tabValue, setTabValue] = useState(activePlan?.interval || "yearly");

  useEffect(() => {
    captureEvent("BP_PageView");
  }, [captureEvent]);

  useEffect(() => {
    if (activePlan?.interval) {
      setTabValue(activePlan.interval);
    }
  }, [activePlan?.interval]);

  const handleTabChange = useCallback(
    (value) => {
      setTabValue(value);
      captureEvent("BP_TabChange", {
        tab_value: value,
      });
    },
    [captureEvent]
  );

  const handleUpgradeScroll = useCallback(() => {
    captureEvent("BP_ClickUpgrade");
    document.getElementById("billing_plans")?.scrollIntoView({ behavior: "smooth" });
  }, [captureEvent]);

  return (
    <Layout>
      <PageHeader heading="Plans" description="View and monitor credit usage across the workspace" />
      <BillingOverview
        clickUpgrade={(e) => {
          e.preventDefault();
          handleUpgradeScroll();
        }}
      />
      <BillingTabs activeTab="Plans" />

      <Tabs value={tabValue} onValueChange={handleTabChange} className="w-full" id="billing_plans">
        <TabsList>
          <TabsTrigger value="yearly">
            Yearly
            <Badge className="ml-2">Upto 10% off</Badge>
          </TabsTrigger>
          <TabsTrigger value="monthly">Monthly</TabsTrigger>
          <TabsTrigger value="one_time">One-time</TabsTrigger>
        </TabsList>
        <TabsContent value="yearly" className="w-full">
          <PricingCards plans={plans} interval="yearly" activePlan={activePlan} />
        </TabsContent>
        <TabsContent value="monthly" className="w-full">
          <PricingCards plans={plans} interval="monthly" activePlan={activePlan} />
        </TabsContent>
        <TabsContent value="one_time" className="w-full">
          <OneTime activeWorkspace={activeWorkspace} />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
